import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { IonAlert, IonButton, IonIcon, IonItem, IonLabel, IonList, IonRadio, IonRadioGroup } from '@ionic/react';
import { trashBinOutline } from 'ionicons/icons';
import { Capacitor } from '@capacitor/core';
import { CapCheckoutPayments } from 'cap-checkout-payment';
import { getConfig } from '../../appConfig';
import Basket from '../../lib/basket';
import api from '../../lib/api';
import { Spacer, StrongText } from '../common';
import { ReactComponent as CardDefault } from '../../assets/images/card-default.svg';
import './styles.css';
import { useDispatch, useSelector } from 'react-redux';
import { loading } from '../../store/actions';
import { getPaymentCards, removePaymentCard } from '../../store/orders/actions';


let checkoutPaymentInitPromise = null;
let cardsFetched = false;
let resolvedPublicKey = null;
let googlePayScriptLoaded = false;

const loadGooglePayScript = () => {
	if (googlePayScriptLoaded || window.google?.payments?.api) {
		return Promise.resolve();
	}
	return new Promise((resolve, reject) => {
		const script = document.createElement('script');
		script.src = 'https://pay.google.com/gp/p/js/pay.js';
		script.onload = () => {
			googlePayScriptLoaded = true;
			resolve();
		};
		script.onerror = () => reject(new Error('Failed to load Google Pay script'));
		document.head.appendChild(script);
	});
};

const CheckoutPayButtons = ({
	__,
	disabled = false,
	createOrderCb = null,
	restaurant = null,
	totalPrice = null,
	hideGooglePay = false,
	hideApplePay = false,
	hideCardPayment = false,
	onBeforePay = null
}) => {

	const dispatch = useDispatch();
	const clientProfile = useSelector((state) => state.common.clientProfile);
	const profile = useSelector((state) => state.profile.profile);
	const cards = useSelector((state) => state.orders.cards);
	const [internalSelectedCard, setInternalSelectedCard] = useState(null);
	const [removeCardAlert, setRemoveCardAlert] = useState(null);
	const [applePayAvailable, setApplePayAvailable] = useState(false);
	const [googlePayAvailable, setGooglePayAvailable] = useState(false);
	const [googlePayButtonReady, setGooglePayButtonReady] = useState(false);
	const googlePayContainerRef = useRef(null);
	useEffect(() => {
		const checkoutPaymentConfig = getConfig()?.checkoutPayment;
		if (!checkoutPaymentConfig?.enabled) {
			return;
		}

		if (!cardsFetched) {
			cardsFetched = true;
			dispatch(getPaymentCards());
		}

		const checkPaymentAvailability = async () => {
			if (!checkoutPaymentInitPromise) {
				checkoutPaymentInitPromise = (async () => {
					// Public key comes from the backend (same endpoint the Stripe flow uses,
					// for checkout clients it contains the checkout.com public key);
					// config value acts as an override. Sandbox is inferred from the prefix.
					const publicKey = checkoutPaymentConfig?.publicKey || (await api.getPublicStripeKey());
					if (!publicKey) {
						throw new Error('Checkout public key not available');
					}
					resolvedPublicKey = publicKey;
					await CapCheckoutPayments.initialize({
						publicKey,
						environment: publicKey.startsWith('pk_sbox_') ? 'sandbox' : 'production'
					});
				})().catch((error) => {
					console.error('Checkout payment initialization error:', error);
					checkoutPaymentInitPromise = null;
					throw error;
				});
			}
			try {
				await checkoutPaymentInitPromise;
			} catch {
				return;
			}

			const platform = Capacitor.getPlatform();
			if (!hideApplePay) {
				const { applePayOnNative, merchantId, countryCode, currencyCode } = checkoutPaymentConfig;
				if (platform === 'ios' && applePayOnNative) {
					try {
						const { canUseApplePay } = await CapCheckoutPayments.canUseApplePay();
						if (canUseApplePay) {
							await CapCheckoutPayments.initializeApplePay({
								merchantId: merchantId || 'merchant.com.hybriddirectdev',
								countryCode: countryCode || 'GB',
								currencyCode: currencyCode || 'GBP'
							});
							setApplePayAvailable(true);
						}
					} catch (error) {
						console.error('Apple Pay initialization error:', error);
					}
				}
			}

			if (!hideGooglePay) {
				const { googlePayOnNative, countryCode, currencyCode } = checkoutPaymentConfig;
				if (platform === 'android' && googlePayOnNative) {
					try {
						const { canUseGooglePay } = await CapCheckoutPayments.canUseGooglePay();
						if (canUseGooglePay) {
							await CapCheckoutPayments.initializeGooglePay({
								countryCode: countryCode || 'GB',
								currencyCode: currencyCode || 'GBP',
								merchantName: restaurant?.name
							});
							setGooglePayAvailable(true);
						}
					} catch (error) {
						console.error('Google Pay initialization error:', error);
					}
				}
			}
		};

		checkPaymentAvailability();
	}, []);

	const handleApplePayClick = async () => {
		if (!applePayAvailable) {
			return;
		}
		if (onBeforePay && onBeforePay() === false) return;

		try {
			const totalAmount = parseInt(totalPrice || Basket.getTotal() * 100);
			const appName = clientProfile?.buisiness_name || 'App';
			const merchantName = restaurant?.name || 'Total';
			const totalLabel = `${merchantName} (via ${appName})`;
			const result = await CapCheckoutPayments.startApplePay({
				totalAmount: totalAmount,
				totalLabel: totalLabel,
				lineItems: [
					{ label: 'Total', amount: totalAmount }
				]
			});
			if (result?.token) {
				if (createOrderCb) {
					createOrderCb({
						selectedCard: result.card,
						nonce: result.token,
						isSelectedCard: false,
						isApplePay: true,
						isGooglePay: false
					});
				} else {
					console.warn('createOrderCb is not defined');
				}
			}
		} catch (error) {
			console.error('Apple Pay Error:', error);
		}
	};

	const handleGooglePayClick = async () => {
		if (!googlePayAvailable) {
			return;
		}
		if (onBeforePay && onBeforePay() === false) return;
		dispatch(loading(true));
		try {
			const totalAmount = parseInt((totalPrice || Basket.getTotal()) * 100);
			const result = await CapCheckoutPayments.startGooglePay({
				totalAmount: totalAmount
			});
			if (result?.token) {
				if (createOrderCb) {
					createOrderCb({
						selectedCard: result.card,
						nonce: result.token,
						isSelectedCard: false,
						isApplePay: false,
						isGooglePay: true
					});
				}
			}
		} catch (error) {
			console.error('Google Pay Error:', error);
		}
		dispatch(loading(false));
	};

	const renderGooglePayButton = useCallback((container) => {
		if (!container || !googlePayAvailable || googlePayButtonReady) return;
		googlePayContainerRef.current = container;

		const checkoutPaymentConfig = getConfig()?.checkoutPayment;
		const isSandbox = !resolvedPublicKey || resolvedPublicKey.startsWith('pk_sbox_');

		loadGooglePayScript().then(() => {
			if (!window.google?.payments?.api) return;
			const client = new window.google.payments.api.PaymentsClient({
				environment: isSandbox ? 'TEST' : 'PRODUCTION'
			});
			const button = client.createButton({
				onClick: handleGooglePayClick,
				buttonColor: checkoutPaymentConfig?.googleButtonColor || 'black',
				buttonSizeMode: 'fill',
				buttonType: 'pay',
				buttonRadius: 30
			});
			container.innerHTML = '';
			container.appendChild(button);
			setGooglePayButtonReady(true);
		}).catch((error) => {
			console.error('Failed to render Google Pay button:', error);
		});
	}, [googlePayAvailable, googlePayButtonReady]);

	useEffect(() => {
		if (cards && cards.length > 0 && !cards.find((card) => card.id === internalSelectedCard)) {
			setInternalSelectedCard(cards[0].id);
		}
	}, [cards]);

	const handlePayWithSelectedCard = () => {
		if (onBeforePay && onBeforePay() === false) return;
		if (createOrderCb) {
			createOrderCb({
				selectedCard: { id: internalSelectedCard },
				nonce: internalSelectedCard,
				isSelectedCard: true,
				isApplePay: false,
				isGooglePay: false
			});
		}
	};

	const handleRemoveCard = () => {
		if (removeCardAlert) {
			dispatch(removePaymentCard(removeCardAlert, { __, cb: () => setRemoveCardAlert(null) }));
		}
	};

	// Card entry tokens are single-use; the card itself is saved on the checkout.com
	// customer after the payment, so it shows up in the list next time.
	const handleCardPayClick = async () => {
		if (onBeforePay && onBeforePay() === false) return;
		const result = await CapCheckoutPayments.startCardEntry({
			payButtonTitle: `Pay`,
			tintColor: getConfig()?.checkoutPayment?.tintColor || '#000000'
		});
		if (result && result?.token) {
			if (createOrderCb) {
				createOrderCb({
					selectedCard: result.card,
					nonce: result.token,
					isSelectedCard: false,
					isApplePay: false,
					isGooglePay: false
				});
			}
		}
	};

	return (
		<>
			<div className="checkout-pay-buttons-container">
				{!hideCardPayment ? (
					<>
						{!profile?.is_guest && cards && cards.length > 0 ? (
							<>
								<StrongText className="block text-to-update">{__('Select a payment card')}</StrongText>
								<Spacer size={2} />
								{/* keyed by the card ids: removing a card remounts the list instead of
								    deleting one IonItem, which Ionic has already detached (removeChild error) */}
								<IonList lines="none" key={cards.map((card) => card.id).join(',')}>
									<IonRadioGroup value={internalSelectedCard}>
										{cards.map((card) => {
											const { id, last4, brand, exp_month, exp_year } = card;
											return (
												<IonItem lines="none" className="box-content checkout-card cards-list-card" key={id}>
													<div className="card-icon-wrapper">
														<CardDefault />
													</div>
													<IonLabel className="ion-text-wrap" onClick={() => setInternalSelectedCard(id)}>
														<StrongText>{__(brand)} - {exp_month}/{exp_year}</StrongText>
														<Spacer size={0} />
														<StrongText className="block">**** **** **** {last4}</StrongText>
													</IonLabel>
													<IonButton className="remove-card-button" fill="clear" slot="end" onClick={() => setRemoveCardAlert(id)}>
														<IonIcon style={{ height: '20px', width: '20px' }} icon={trashBinOutline} className="remove-card-icon" />
													</IonButton>
													<IonRadio color="primary" slot="start" value={id} onIonSelect={() => setInternalSelectedCard(id)} />
												</IonItem>
											);
										})}
									</IonRadioGroup>
								</IonList>
								<Spacer size={2} />
								<IonButton
									disabled={disabled || !internalSelectedCard}
									style={{ marginBottom: '10px' }}
									className="button-uppercase"
									onClick={handlePayWithSelectedCard}
									expand="block"
								>
									{getConfig()?.checkoutPayment?.label || __('Pay')}
								</IonButton>
								<Spacer size={2} />
							</>
						) : null}
						<IonButton
							style={{ marginBottom: '10px' }}
							className="button-uppercase"
							onClick={handleCardPayClick}
							expand="block"
							disabled={disabled}
							fill={!profile?.is_guest && cards && cards.length > 0 ? 'clear' : 'solid'}
							color={!profile?.is_guest && cards && cards.length > 0 ? 'dark' : 'primary'}
						>
							{!profile?.is_guest && cards && cards.length > 0 ? __('Pay with New Card') : (getConfig()?.checkoutPayment?.label || __('Pay with Card'))}
						</IonButton>
						<Spacer size={1} />
					</>
				) : null}
				<IonAlert
					isOpen={!!removeCardAlert}
					onDidDismiss={() => setRemoveCardAlert(null)}
					header={__('Confirm')}
					message={__('Do you you want to remove this card?')}
					buttons={[
						{ text: __('Cancel'), role: 'cancel', cssClass: 'secondary' },
						{ text: __('Remove'), handler: handleRemoveCard }
					]}
				/>
				{/* Apple Pay Button */}
				{applePayAvailable && (
					<>
						<button
							type="button"
							className="apple-pay-button"
							onClick={handleApplePayClick}
							disabled={disabled}
							aria-label="Apple Pay"
						>
							<svg viewBox="0 0 170 170" className="apple-pay-logo">
								<path fill="#fff" d="M150.4 130.3c-2.4 5.6-5.2 10.8-8.4 15.6-4.4 6.5-7.9 11-10.7 13.5-4.3 4.1-8.8 6.2-13.7 6.4-3.5 0-7.7-1-12.6-3-4.9-2-9.4-3-13.5-3-4.4 0-9 1-14 3-5 2-9 3-12.1 3.1-4.6.2-9.2-1.9-13.8-6.4-3-2.7-6.7-7.3-11.2-13.9-4.8-7.1-8.7-15.3-11.8-24.7-3.3-10.1-5-19.9-5-29.5 0-10.9 2.4-20.3 7.1-28.2 3.7-6.3 8.6-11.3 14.8-15 6.2-3.6 12.8-5.5 20-5.6 3.7 0 8.6 1.1 14.7 3.4 6 2.2 9.9 3.4 11.6 3.4 1.3 0 5.6-1.3 12.9-4 6.9-2.5 12.7-3.5 17.5-3.1 12.9 1 22.6 6.1 29 15.2-11.6 7-17.3 16.8-17.2 29.3.1 9.7 3.6 17.8 10.6 24.2 3.1 3 6.6 5.3 10.5 6.9-.8 2.4-1.7 4.7-2.7 6.9zM119.3 7c0 7.6-2.8 14.8-8.3 21.3-6.6 7.8-14.7 12.3-23.4 11.6-.1-.9-.2-1.9-.2-2.9 0-7.3 3.2-15.2 8.9-21.6 2.8-3.2 6.4-5.9 10.8-8 4.3-2.1 8.4-3.2 12.3-3.4.1 1 .2 2 .2 3z" />
							</svg>
							<span className="apple-pay-text">Pay</span>
						</button>
					</>
				)}

				{/* Google Pay Button - Official Google createButton API */}
				{googlePayAvailable && (
					<div
						ref={renderGooglePayButton}
						className="google-pay-button-container"
						style={{ opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto' }}
					/>
				)}
			</div>
		</>
	);
};

CheckoutPayButtons.propTypes = {
	__: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
};

export default CheckoutPayButtons;
